// Password validation component with real-time feedback
import { CheckCircle, XCircle, ShieldCheck } from 'lucide-react';

/**
 * Password requirements:
 * - 8-12 characters
 * - At least one uppercase letter
 * - At least one number
 * - At least one special character
 */
export const passwordRequirements = [
  { 
    label: '8-12 characters', 
    test: (pwd) => pwd.length >= 8 && pwd.length <= 12 
  },
  {
    label: 'At least one uppercase letter',
    test: (pwd) => /[A-Z]/.test(pwd)
  },
  { 
    label: 'Atleast one number', 
    test: (pwd) => /[0-9]/.test(pwd) 
  },
  { 
    label: 'Atleast one special character (!@#$%^&*)', 
    test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd) 
  },
];

export const isPasswordValid = (password) => {
  return passwordRequirements.every(req => req.test(password));
};

export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }
  if (password.length > 12) {
    errors.push('Password must be at most 12 characters');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  return errors;
};

export default function PasswordValidation({ password, showValidation = true }) {
  if (!showValidation || !password) return null;

  const allValid = isPasswordValid(password);

  return (
    <div className={`mt-2 p-3 rounded-lg border ${
      allValid 
        ? 'bg-green-50 border-green-200' 
        : 'bg-red-50 border-red-200'
    }`}>
      {allValid ? (
        <div className="flex items-center gap-2 text-green-700">
          <ShieldCheck className="w-5 h-5" />
          <span className="font-medium">Safe and sound!</span>
        </div>
      ) : (
        <>
          <p className="text-sm font-medium text-red-700 mb-2">Password must contain:</p>
          <ul className="space-y-1">
            {passwordRequirements.map((req, idx) => {
              const isValid = req.test(password);
              return (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  {isValid ? (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  )}
                  <span className={isValid ? 'text-green-700' : 'text-red-600'}>
                    {req.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
